import { ArrowUpRight16 } from '@carbon/icons-react';
import TreeView16 from '@carbon/icons-react/es/tree-view/16';
import { Tooltip } from 'antd';
import { dropRight, memoize } from 'lodash';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { StatusFieldOption, StatusGroup, Task, TaskType } from 'api/tasks';
import { ReactComponent as FormTemplateIconSvg } from 'assets/svgs/form-instance-icon.svg';
import { Button } from 'components/atoms/button';
import { EmptyCell } from 'components/atoms/empty-cell';
import { NewRow } from 'components/molecules/new-row';
import { StatusIndicator } from 'components/molecules/status-indicator';

interface Data {
  taskName: string;
  status: number;
  progress: number;
  filtered: boolean;
  taskTree: Task[];
  taskId: number | string;
  type?: TaskType;
  formInstanceId?: number;
}

export const statusHtmlForOptions = (options: StatusFieldOption[]) => {
  const _get = memoize((status: number) =>
    options.find((x) => x.value === status),
  );

  return memoize(
    (type: TaskType, status: number, progress?: number) => {
      const selected = _get(status);

      return selected !== undefined ? (
        <StatusIndicator
          className="status-indicator task-field-value"
          type={type}
          status={selected}
          progress={progress}
        />
      ) : (
        <EmptyCell />
      );
    },
    (type: TaskType, status: number, progress?: number) => {
      const selected = _get(status);

      return `${type}, ${status}, ${
        selected?.group === StatusGroup.InProgress ? progress : ''
      }`;
    },
  );
};

const AddTask = styled(NewRow)`
  .icon-shell {
    margin-left: -10px;
  }

  input {
    background: transparent;
  }
`;
const TaskTitleWrapper = styled.span`
  display: flex;
  gap: var(--spacing-02);
  cursor: pointer;
`;

const StyledArrow = styled(ArrowUpRight16)`
  color: var(--blue-60);
`;

const StyledArrowButton = styled(Button)`
  position: absolute;
  right: 10px;
`;

export const TaskFieldValue = (options) => {
  const statusHtmlFor = statusHtmlForOptions(options);
  const { pathname } = window.location;
  const isSprintPage = pathname.includes('/sprints');

  return ({ data, node }) => {
    const {
      status,
      progress,
      taskTree,
      taskName = '',
      taskId,
      type = TaskType.Task,
      formInstanceId,
    } = data as Data;

    if (taskId === 'provisional') {
      return (
        <AddTask
          id="add-task"
          autoFocus={false}
          onAdd={() => Promise.resolve()}
          placeholder="Add new task"
          bulkAddType="tasks"
        />
      );
    }
    const statusHtml = statusHtmlFor(type, status, progress);
    const treeTile = dropRight(taskTree, 1)
      .map((x) => x.taskName)
      .join(' / ');

    const treeHtml =
      taskTree?.length > 1 || (node?.level != null && node?.level > 0) ? (
        <span title={treeTile}>
          <TreeView16 />
        </span>
      ) : (
        ''
      );

    const nameHtml = (
      <TaskTitleWrapper
        title={taskName}
        data-cy={`grid.task-title.${taskName}`}
      >
        {formInstanceId ? <FormTemplateIconSvg /> : null}
        {taskName}
      </TaskTitleWrapper>
    );

    return (
      <div className="spacer spacer-medium">
        {statusHtml}
        {treeHtml}
        {nameHtml}
        {isSprintPage && (
          <Tooltip title={<FormattedMessage id="tasks.open" />}>
            <StyledArrowButton
              className="arrow"
              type="text"
              icon={<StyledArrow />}
              size="small"
            />
          </Tooltip>
        )}
      </div>
    );
  };
};
